import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { DownloadIcon } from '../../../../../icons/SvgIcons';
import { PrimaryButton } from '../../../../ui/Button/Buttons';

type ExportFormat = 'csv' | 'json' | 'pdf';

interface ExportDropdownProps {
  onExport: (format: ExportFormat) => void;
}

export function ExportDropdown({ onExport }: ExportDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [menuPosition, setMenuPosition] = useState({
    top: 0,
    left: 0
  });

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX
      });
    }
  }, [isOpen]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node) &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // close dropdown on scroll
  useEffect(() => {
    const handleScroll = () => {
      setIsOpen(false);

      const button = buttonRef.current?.querySelector('button');
      button?.blur();

      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    };

    window.addEventListener('scroll', handleScroll, true);

    return () => {
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, []);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleOptionClick = (format: ExportFormat) => {
    onExport(format);
    setIsOpen(false);
  };

  const exportOptions = [
    { value: 'csv' as ExportFormat, label: 'CSV' },
    { value: 'json' as ExportFormat, label: 'JSON' },
    { value: 'pdf' as ExportFormat, label: 'PDF' }
  ];

  return (
    <div ref={buttonRef} className="relative">
      <PrimaryButton onClick={handleToggle} icon={<DownloadIcon />} size="xs">
        Download Log
      </PrimaryButton>

      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            style={{
              position: 'absolute',
              top: `${menuPosition.top}px`,
              left: `${menuPosition.left}px`,
              width: '126px',
              zIndex: 9999
            }}
            className="mt-1 bg-white rounded-md shadow-lg"
          >
            {exportOptions.map((option) => (
              <div
                key={option.value}
                onClick={(e) => {
                  e.stopPropagation();
                  handleOptionClick(option.value);
                }}
                className="px-4 py-2 cursor-pointer font-inter font-normal text-sm leading-5 text-[#4E4E56] hover:bg-[#F7F7F8] flex items-center"
                style={{ gap: '8px' }}
              >
                {option.label}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}
